import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  clearTraceDataAndMessages,
  subscribeToAnalyzers,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { selectedWallChanged, toggleSingleTraceView } from './wallsSlice';
import {
  Collapse,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { useWallsWithoutFolders } from './api/getWallsWithoutFolders';
import { useWallsByFolder } from './api/getWallsByFolder';
import { useWindows } from 'features/windows/api/getWindows';

const WallList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedWallId = useAppSelector((state) => state.walls.selectedWallId);
  const { data: windows } = useWindows({ wallId: selectedWallId });
  const { user } = useAppSelector((state) => state.auth);

  const { data: folders } = useWallsByFolder({ userId: user?.id || -1 });

  const wallsWithoutFolders = useWallsWithoutFolders({
    userId: user?.id || -1,
  });

  const [foldersOpenStatus, setFoldersOpenStatus] = useState<{
    [id: number]: boolean;
  }>({});

  useEffect(() => {
    if (!folders) return;
    const newOpenStatus: { [id: number]: boolean } = {};
    for (const folder of folders) {
      if (!(folder.id in foldersOpenStatus)) newOpenStatus[folder.id] = false;
      else newOpenStatus[folder.id] = foldersOpenStatus[folder.id];
    }
    setFoldersOpenStatus(newOpenStatus);
    //eslint-disable-next-line
  }, [folders]);

  const handleWallClicked = (wallId: number) => {
    dispatch(clearTraceDataAndMessages());
    dispatch(toggleSingleTraceView(false));
    dispatch(selectedWallChanged(wallId));
    navigate(`/wall/${wallId}`);
  };

  useEffect(() => {
    if (selectedWallId === -1 || !windows || !user?.id) return;
    const analyzerIds = windows.map((window) => window.analyzer_id);
    dispatch(
      subscribeToAnalyzers({
        user_id: user.id,
        analyzer_ids: analyzerIds,
      })
    );
  }, [dispatch, selectedWallId, user?.id, windows]);

  const handleFolderClicked = (folderId: number) => {
    const currentFolderStatus = foldersOpenStatus[folderId];
    const newFolderStatusState = { ...foldersOpenStatus };
    newFolderStatusState[folderId] = !currentFolderStatus;
    setFoldersOpenStatus(newFolderStatusState);
  };

  return (
    <div>
      <List>
        {folders?.map((folder) => (
          <Fragment key={folder.id + 'folder'}>
            <ListItemButton
              onClick={() => handleFolderClicked(folder.id)}
              key={folder.id + 'folder'}
            >
              <ListItemText>
                <Grid container alignItems='center'>
                  {folder.name}
                </Grid>
              </ListItemText>
              {foldersOpenStatus[folder.id] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={foldersOpenStatus[folder.id]}
              timeout='auto'
              unmountOnExit
            >
              {folder.walls.map((wall) => (
                <ListItemButton
                  onClick={() => handleWallClicked(wall.id)}
                  key={wall.id + 'wall'}
                  selected={selectedWallId === wall.id}
                  sx={{ pl: '2rem' }}
                >
                  {wall.synched && (
                    <ListItemIcon>
                      <PublicIcon />
                    </ListItemIcon>
                  )}
                  <ListItemText>{wall.name}</ListItemText>
                </ListItemButton>
              ))}
            </Collapse>
          </Fragment>
        ))}
        {wallsWithoutFolders?.data?.map((wall: any) => (
          <ListItemButton
            onClick={() => handleWallClicked(wall.id)}
            key={wall.id + 'wallnofolder'}
            selected={selectedWallId === wall.id}
          >
            {wall.synched && (
              <ListItemIcon>
                <PublicIcon />
              </ListItemIcon>
            )}
            <ListItemText>{wall.name}</ListItemText>
          </ListItemButton>
        ))}
      </List>
    </div>
  );
};

export default WallList;
